import { useState, useEffect, useCallback } from 'react';

export type CookiePreferences = {
  necessary: boolean; // Always true, required for site functionality
  analytics: boolean;
  marketing: boolean;
};

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'customized';

const COOKIE_CONSENT_KEY = 'easy-taxes-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'easy-taxes-cookie-preferences';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const useCookieConsent = () => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved preferences on mount
  useEffect(() => {
    const savedStatus = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus | null;
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (savedStatus) {
      setConsentStatus(savedStatus);
    }

    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences) as CookiePreferences;
        setPreferences({ ...parsed, necessary: true }); // Ensure necessary is always true
      } catch {
        // Invalid JSON, use defaults
      }
    }

    setIsLoaded(true);
  }, []);

  const saveConsent = useCallback((status: ConsentStatus, prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, status);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setConsentStatus(status);
    setPreferences(prefs);
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent('accepted', allAccepted);
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    const allRejected: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent('rejected', allRejected);
  }, [saveConsent]);

  const saveCustomPreferences = useCallback((customPrefs: Partial<CookiePreferences>) => {
    const newPrefs: CookiePreferences = {
      necessary: true, // Always true
      analytics: customPrefs.analytics ?? false,
      marketing: customPrefs.marketing ?? false,
    };
    saveConsent('customized', newPrefs);
  }, [saveConsent]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setConsentStatus('pending');
    setPreferences(defaultPreferences);
  }, []);

  const showBanner = isLoaded && consentStatus === 'pending';

  return {
    consentStatus,
    preferences,
    isLoaded,
    showBanner,
    acceptAll,
    rejectAll,
    saveCustomPreferences,
    resetConsent,
  };
};
